import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/** Footer newsletter capture. Posts to the backend, which talks to Mailchimp. */
export const NewsletterSignup = () => {
    const [cfg, setCfg] = useState({ enabled: false, double_opt_in: true });
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState("");
    const [coupon, setCoupon] = useState(null);

    useEffect(() => {
        api.get("/newsletter/config")
            .then(({ data }) => setCfg(data))
            .catch(() => setCfg({ enabled: false, double_opt_in: true }));
    }, []);

    if (!cfg.enabled) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setBusy(true);
        try {
            const { data } = await api.post("/newsletter/subscribe", { email: email.trim() });
            setDone(data.message);
            setCoupon(data.coupon || null);
            setEmail("");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not sign you up. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="newsletter-signup">
            <div className="label-eyebrow text-stone-400 mb-4">Newsletter</div>
            {done ? (
                <div className="space-y-3" data-testid="newsletter-success">
                    <div className="flex items-start gap-2 text-sm text-emerald-400">
                        <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>{done}</span>
                    </div>
                    {coupon && (
                        <div className="border border-emerald-700/60 bg-emerald-950/40 p-4" data-testid="newsletter-coupon">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400">
                                Your {coupon.pct}% welcome code
                            </div>
                            <div className="font-mono text-xl text-stone-50 mt-1 tracking-wider" data-testid="newsletter-coupon-code">
                                {coupon.code}
                            </div>
                            <div className="text-[11px] text-stone-400 mt-1">
                                Valid until {coupon.valid_until} · one use · no minimum order
                                {coupon.emailed ? " · also sent to your inbox" : ""}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <>
                    <p className="text-sm text-stone-400 leading-relaxed mb-3">
                        Remedy guides, seasonal care notes and subscriber-only offers. No spam.
                    </p>
                    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
                        <div className="relative flex-1">
                            <Mail className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@email.com"
                                aria-label="Email address"
                                className="w-full bg-stone-800 border border-stone-700 text-stone-100 placeholder:text-stone-500 text-sm pl-9 pr-3 py-2.5 focus:outline-none focus:border-sky-500 transition-colors"
                                data-testid="newsletter-email-input"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={busy}
                            className="bg-sky-700 hover:bg-sky-600 disabled:opacity-60 text-stone-50 text-sm px-5 py-2.5 transition-colors whitespace-nowrap"
                            data-testid="newsletter-submit"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Subscribe"}
                        </button>
                    </form>
                    {cfg.double_opt_in && (
                        <p className="text-[10px] text-stone-500 mt-2">
                            We'll email you a confirmation link to complete your signup.
                        </p>
                    )}
                </>
            )}
        </div>
    );
};
